import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { useAuditTail } from "@/hooks/useAuditTail";
import { auditTarget, formatAuditSummary } from "@/lib/audit-format";
import { formatDateTime } from "@/lib/datetime";

const VISIBLE = 10;

export function RecentChangesWidget(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, error } = useAuditTail(VISIBLE);
  const entries = (data ?? []).slice(0, VISIBLE);

  return (
    <Card data-testid="widget-recent-changes">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("home.widgets.recentChanges.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <ul className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <li key={i} className="bg-muted/40 h-5 animate-pulse rounded" />
            ))}
          </ul>
        ) : isError ? (
          <ApiErrorNotice
            error={error}
            fallbackMessage={t("common.apiError.fallback")}
            className="text-destructive text-sm"
          />
        ) : entries.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("home.widgets.recentChanges.empty")}</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {entries.map((entry, idx) => {
              const target = auditTarget(entry);
              const summary = formatAuditSummary(entry, t);
              const time = formatDateTime(entry.occurred_at, i18n.resolvedLanguage);
              const key = entry.id ?? `${entry.occurred_at}-${idx}`;
              const content = (
                <>
                  <span className="text-muted-foreground shrink-0 tabular-nums">{time}</span>
                  <span className="text-muted-foreground shrink-0">·</span>
                  <span className="text-muted-foreground truncate">{entry.actor}</span>
                  <span className="text-muted-foreground shrink-0">·</span>
                  <span className="truncate">{summary}</span>
                </>
              );
              const linkClass = "hover:bg-accent flex items-center gap-1.5 rounded px-1.5 py-1";
              return (
                <li
                  key={key}
                  data-testid={`widget-recent-row-${entry.id ?? "anon"}`}
                  className="text-xs"
                >
                  {target.to === "/approvals/$simulationId" ? (
                    <Link
                      to="/approvals/$simulationId"
                      params={{ simulationId: Number(target.params.simulationId ?? 0) }}
                      className={linkClass}
                    >
                      {content}
                    </Link>
                  ) : target.to === "/projects/$projectId" ? (
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: String(target.params.projectId ?? "") }}
                      className={linkClass}
                    >
                      {content}
                    </Link>
                  ) : (
                    <span className="flex items-center gap-1.5 px-1.5 py-1">{content}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
